import { lazy, Suspense, useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import BottomNav from "@/components/BottomNav";
import { useSwipeBack } from "@/hooks/useSwipeBack";
import Index from "./pages/Index";

// Route imports — prefetched on idle for instant navigation
const pageImports = {
  sleep: () => import("./pages/SleepPage"),
  feeding: () => import("./pages/FeedingPage"),
  tips: () => import("./pages/TipsPage"),
  recipes: () => import("./pages/RecipesPage"),
  lullabies: () => import("./pages/LullabiesPage"),
  games: () => import("./pages/GamesPage"),
  sleepRoutine: () => import("./pages/SleepRoutinePage"),
  medicine: () => import("./pages/MedicinePage"),
  stories: () => import("./pages/StoriesPage"),
  auth: () => import("./pages/AuthPage"),
  contact: () => import("./pages/ContactPage"),
  sleepGuide: () => import("./pages/BabySleepGuide"),
  feedingGuide: () => import("./pages/BabyFeedingGuide"),
  notFound: () => import("./pages/NotFound"),
};

const SleepPage = lazy(pageImports.sleep);
const FeedingPage = lazy(pageImports.feeding);
const TipsPage = lazy(pageImports.tips);
const RecipesPage = lazy(pageImports.recipes);
const LullabiesPage = lazy(pageImports.lullabies);
const GamesPage = lazy(pageImports.games);
const SleepRoutinePage = lazy(pageImports.sleepRoutine);
const MedicinePage = lazy(pageImports.medicine);
const StoriesPage = lazy(pageImports.stories);
const AuthPage = lazy(pageImports.auth);
const ContactPage = lazy(pageImports.contact);
const BabySleepGuide = lazy(pageImports.sleepGuide);
const BabyFeedingGuide = lazy(pageImports.feedingGuide);
const NotFound = lazy(pageImports.notFound);

// Prefetch only the most likely next pages after a delay to avoid hurting TTI
if (typeof window !== "undefined") {
  const prefetchKey = () => {
    // Only prefetch the 3 most-visited routes; others load on demand
    pageImports.sleep();
    pageImports.feeding();
    pageImports.tips();
  };
  // Wait until well after interactive before prefetching
  window.addEventListener("load", () => {
    setTimeout(prefetchKey, 4000);
  });
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1 } },
});

const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <span className="text-muted-foreground animate-pulse">Loading...</span>
  </div>
);

const HIDE_NAV_ROUTES = ["/auth"];

const AppRoutes = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  useSwipeBack();

  // Scroll to top on route change to prevent stale scroll positions
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (loading) return <PageLoader />;

  const showNav = !HIDE_NAV_ROUTES.includes(location.pathname);

  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/auth" element={user ? <Navigate to="/" replace /> : <AuthPage />} />
          <Route path="/" element={<Index />} />
          <Route path="/baby-sleep" element={<SleepPage />} />
          <Route path="/baby-sleep/routine" element={<SleepRoutinePage />} />
          <Route path="/baby-feeding" element={<FeedingPage />} />
          <Route path="/baby-tips" element={<TipsPage />} />
          <Route path="/baby-recipes" element={<RecipesPage />} />
          <Route path="/baby-lullabies" element={<LullabiesPage />} />
          <Route path="/baby-games" element={<GamesPage />} />
          <Route path="/baby-medicine" element={<MedicinePage />} />
          <Route path="/baby-stories" element={<StoriesPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/baby-sleep-guide" element={<BabySleepGuide />} />
          <Route path="/baby-feeding-guide" element={<BabyFeedingGuide />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      {showNav && <BottomNav />}
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <LanguageProvider>
        <AuthProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
